/**
 * Talent announcements (prototype v108 §5.1): "Broadcast a message to all talent or limit by ZIP/radius. Bulk direct
 * messaging is handled from the Talent table below." Pure: the talent list in, who receives it out.
 *
 * Goes to talent in Good Standing only (Incomplete profiles and Blacklisted talent are left out). With a ZIP, only
 * talent whose home is within the radius (10–300 miles, default 50); talent with no home coordinates are left out.
 * Delivered as an in-portal message from the staff member, like the bulk message.
 */
import { talentStatus } from './talentStatus.js'
import { milesBetween } from './geo.js'

export const RADIUS = { min: 10, max: 300, step: 10, default: 50 }
export const MAX_LENGTH = 4000

export { milesBetween }

/**
 * @param input      { message, zip?, radius? }
 * @param zipLookup  zip -> { latitude, longitude, city, state } | null
 */
export function planAnnouncement(input = {}, zipLookup = () => null) {
  const errors = []
  const message = String(input.message || '').trim()
  if (!message) errors.push('Type the announcement')
  if (message.length > MAX_LENGTH) errors.push(`Keep it under ${MAX_LENGTH.toLocaleString('en-US')} characters`)
  const zip = String(input.zip || '').trim()
  let center = null
  let radius = null
  if (zip) {
    if (!/^\d{5}$/.test(zip)) errors.push('ZIP must be 5 digits')
    else {
      const z = zipLookup(zip)
      if (!z || !Number.isFinite(Number(z.latitude)) || !Number.isFinite(Number(z.longitude))) errors.push(`ZIP ${zip} was not found`)
      else center = { lat: Number(z.latitude), lng: Number(z.longitude), label: `${z.city}, ${z.state} ${zip}` }
    }
    const r = Number(input.radius ?? RADIUS.default)
    radius = Number.isFinite(r) ? Math.min(RADIUS.max, Math.max(RADIUS.min, Math.round(r / RADIUS.step) * RADIUS.step)) : RADIUS.default
  }
  return { ok: errors.length === 0, errors, message, center, radius }
}

/**
 * Who receives it: Good Standing talent with a login; within the radius when a ZIP is given.
 * rows: { talent_id, user_id, full_name, city, state, status, is_blacklisted, profile_complete, latitude, longitude }
 */
export function announcementRecipients(rows, { center = null, radius = null } = {}) {
  return (rows || []).filter(r => {
    if (!r.user_id || talentStatus(r) !== 'Good Standing') return false
    if (!center) return true
    const lat = Number(r.latitude)
    const lng = Number(r.longitude)
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0) || r.latitude === null || r.longitude === null) return false
    return milesBetween(center, { lat, lng }) <= radius
  })
}
